import React, { useEffect } from 'react';
import { TVisibilityCond, TypeformVisibilityCond, UserVisibilityCond } from '../../types';
import { checksElements } from '../../utils/reservedContent';
import { UserDataInterface } from '@food/auth/build/dist';
import { useLocalStorage } from '../../utils/misc';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../Spinner';
import { IFNTheme, registerClass, currySC } from '@food/css-manager/build/dist';

interface IntroPageProps {
	labels?: { title?: string, description?: string, cta?: string };
	conditions: ReadonlyArray<TVisibilityCond>;
	onCanView: () => void;
	user: UserDataInterface['userData'];
}

interface IntroPageState {
	loading: boolean;
	possibleGains: ReadonlyArray<string>;
	hasAccess: boolean;
}

const gainAccessPriorities = ['PARDOT', 'TYPEFORM', 'USER'];

const styleClass = registerClass(
	(t: IFNTheme, sc) => `
	display: flex;
	justify-content: center;
	
	> div {
		border-color: ${t.colors.grey.dark};
		border-style: solid;
		border-width: 1px;
		width: 100%;
		max-width: 800px;
	}
	
	.${sc('title')} {
		font-size: ${t.ratios.xl ** 2}em;
		margin: ${t.font.baseSize * t.ratios.l}px ${t.font.baseSize * t.ratios.l}px ${t.font
			.baseSize * t.ratios.xs}px;
		line-height: 1;
	}
	
	.${sc('intro')} {
		margin: 0 ${t.font.baseSize * t.ratios.l}px ${t.font.baseSize * t.ratios.l}px;
		font-size: ${t.ratios.l}em;
	}
`,
);

const sc = currySC(styleClass);

export const IntroPage: React.FC<IntroPageProps> = ({ labels, conditions, onCanView, user }) => {
	const { t } = useTranslation();
	const [{ loading, possibleGains, hasAccess }, setState] = React.useState<
		IntroPageState
	>({
		loading: true,
		possibleGains: [],
		hasAccess: false,
	});

	useLocalStorage((s) =>
		conditions
			.filter((c): c is TypeformVisibilityCond => c.type === 'TYPEFORM')
			.map((c) => c.achievement)
			.includes(s),
	);

	useEffect(() => {
		// faccio alcuni controlli prima di altri in modo da essere sicuro di non eseguire prima quelli piu' dispendiosi
		Promise.all(
			conditions.map(async (condition) => {
				const definitionBlock = checksElements[condition.type];
				if (!definitionBlock) {
					throw new Error('ERRORACCIO');
				}
				return await definitionBlock.check(condition);
			}),
		).then((results) => {
			const canAccess = results.some((r) => r);

			if (canAccess) {
				setState({ loading: false, possibleGains: [], hasAccess: true });
			} else {
				Promise.all(
					conditions.map(async (c) => {
						const checker = checksElements[c.type]?.canGainAccess || (() => false);
						const canGain = await checker(c);
						return canGain ? c.type : null;
					}),
				).then((gainableTypes) => {
					const cleanedGainableTypes = gainableTypes.filter(
						(c): c is UserVisibilityCond['type'] => {
							return c !== null;
						},
					);

					cleanedGainableTypes.sort(
						(g1, g2) =>
							gainAccessPriorities.indexOf(g1) - gainAccessPriorities.indexOf(g2),
					);

					setState({
						loading: false,
						possibleGains: cleanedGainableTypes,
						hasAccess: false,
					});
				});
			}
		});
	}, [conditions, user]);

	useEffect(() => {
		if (hasAccess) {
			onCanView();
		}
	}, [hasAccess, onCanView]);

	if (hasAccess) {
		return <section />;
	}

	return (
		<div className={styleClass}>
			{loading && <Spinner />}
			{!loading && possibleGains.length === 0 && (
				<div>
					<div className={sc('title')}>{t`access_not_allowed_title`}</div>
					<div
						className={sc('intro')}
						dangerouslySetInnerHTML={{
							__html: t`access_not_allowed_not_gainable_html`,
						}}
					/>
				</div>
			)}
			{!loading && possibleGains.length > 0 && (
				<div>
					<div className={sc('title')}>{labels && labels.title ? labels.title : t`access_not_allowed_title`}</div>
					<div
						className={sc('intro')}
						dangerouslySetInnerHTML={{
							__html: labels && labels.description ? labels.description : t`access_not_allowed_but_gainable_html`,
						}}
					/>

					{possibleGains.map((gainType) => {
						const definitionBlock = checksElements[gainType as keyof typeof checksElements];
						if (!definitionBlock) {
							throw new Error('ERRORE');
						}

						// data una tipologia di permesso prendo la condizione corrispondente
						const condition = conditions.find((c) => c.type === gainType);

						if (condition === undefined) {
							throw new Error('ERRORE'); // TODO
						}

						const C = definitionBlock.gainAccessAction;
						return (
							<div className={sc('gain-action')}>
								<C condition={condition} label={(labels && labels.cta ? labels.cta : null) ?? undefined} />
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
